import { chainEdges, edgePortion, sampleSegment } from "./geometry";
import type { DraftSegment, PointTuple } from "./types";

/** One edge of a closed outline, turned to run on from the one before. */
export interface LoopEdge {
  name: string;
  segments: DraftSegment[];
}

export interface ClippedOutline {
  /** The new outline, in loop order: kept edges whole, cut edges as portions, then the clip line. */
  edges: Array<LoopEdge & { source: string; whole: boolean }>;
  /** The clip line's stretch across the piece. */
  clip: DraftSegment[];
}

// The clip line runs on this far past each end, so a line drawn to end on
// the outline (a raglan line ending on the armhole) still crosses it.
const OVERRUN = 2;
const SAMPLES = 200;

/**
 * Cut a closed outline along a line, as a yoke or raglan line cuts a body
 * piece: where the line crosses the outline first and last, the stretch of
 * outline on the `away` side of the line is replaced by the line itself.
 * Undefined when the line does not cross the outline twice, so the piece
 * stays whole.
 */
export function clipOutline(loop: LoopEdge[], along: DraftSegment[][], away: PointTuple): ClippedOutline | undefined {
  const line = chainEdges(along);
  if (!line || loop.length < 2) return undefined;
  const extended = overrun(line);
  const linePath = path(extended);
  const loopSamples = loop.map((edge) => path(edge.segments));
  const outline = loopSamples.flatMap((samples, edge) => samples.slice(0, -1).map((point, index) => ({ point, next: samples[index + 1], edge, index })));

  // Every crossing, by its position along the line (s) and round the outline (p).
  const crossings: { s: number; p: number; point: PointTuple }[] = [];
  for (let step = 1; step < linePath.length; step += 1) {
    const [a, b] = [linePath[step - 1], linePath[step]];
    outline.forEach(({ point: c, next: d }, position) => {
      const denominator = (b[0] - a[0]) * (d[1] - c[1]) - (b[1] - a[1]) * (d[0] - c[0]);
      if (denominator === 0) return;
      const t = ((c[0] - a[0]) * (d[1] - c[1]) - (c[1] - a[1]) * (d[0] - c[0])) / denominator;
      const u = ((c[0] - a[0]) * (b[1] - a[1]) - (c[1] - a[1]) * (b[0] - a[0])) / denominator;
      if (t >= 0 && t <= 1 && u >= 0 && u <= 1) crossings.push({ s: step - 1 + t, p: position + u, point: mix(a, b, t) });
    });
  }
  if (crossings.length < 2) return undefined;
  crossings.sort((x, y) => x.s - y.s);
  const [first, last] = [crossings[0], crossings[crossings.length - 1]];
  if (Math.hypot(first.point[0] - last.point[0], first.point[1] - last.point[1]) < 0.01) return undefined;

  // The two stretches of outline between the crossings; cut away the one on
  // the away point's side of the line. Each stretch leaves the first crossing
  // on its own side of the line, so the side is read there.
  const total = outline.length;
  const ahead = (from: number, to: number) => (to - from + total) % total;
  const cross = (a: PointTuple, b: PointTuple, point: PointTuple) => (b[0] - a[0]) * (point[1] - a[1]) - (b[1] - a[1]) * (point[0] - a[0]);
  const across = Math.min(Math.floor(first.s), linePath.length - 2);
  const [lineA, lineB] = [linePath[across], linePath[across + 1]];
  let probe = first.point;
  for (let step = 1; step < total && Math.hypot(probe[0] - first.point[0], probe[1] - first.point[1]) < 0.5; step += 1) {
    probe = outline[Math.floor(first.p + step) % total].point;
  }
  const forwardSide = Math.sign(cross(lineA, lineB, probe));
  // The away point is read against the stretch of line nearest it, between the crossings.
  let nearest = Math.max(1, Math.floor(first.s));
  let distance = Infinity;
  for (let index = Math.max(1, Math.floor(first.s)); index <= Math.min(linePath.length - 1, Math.ceil(last.s)); index += 1) {
    const d = Math.hypot(away[0] - linePath[index][0], away[1] - linePath[index][1]);
    if (d < distance) [nearest, distance] = [index, d];
  }
  const awaySide = Math.sign(cross(linePath[nearest - 1], linePath[nearest], away));
  // Leaving `first` forwards runs round to `last` on forwardSide.
  const [start, end] = forwardSide === awaySide ? [last, first] : [first, last];

  // Keep the outline from `start` round to `end`, cutting the edges they fall on.
  const edges: ClippedOutline["edges"] = [];
  const startEdge = outline[Math.floor(start.p) % total].edge;
  const endEdge = outline[Math.floor(end.p) % total].edge;
  const wraps = startEdge === endEdge && ahead(start.p, end.p) > loopSamples[startEdge].length;
  let edge = startEdge;
  for (let visit = 0; visit <= loop.length; visit += 1) {
    const isStart = visit === 0;
    const isEnd = edge === endEdge && (!wraps || visit > 0);
    const { segments, name } = loop[edge];
    const from = isStart ? start.point : segments[0].start;
    const to = isEnd ? end.point : segments[segments.length - 1].end;
    const portion = isStart || isEnd ? edgePortion(segments, from, to, 0.6) : segments;
    if (length(portion) > 0.01) edges.push({ name, source: name, segments: portion, whole: !isStart && !isEnd });
    if (isEnd) break;
    edge = (edge + 1) % loop.length;
  }
  const clip = edgePortion(extended, end.point, start.point, 0.6);
  return { edges, clip };
}

function overrun(segments: DraftSegment[]): DraftSegment[] {
  const head = sampleSegment(segments[0], SAMPLES);
  const tail = sampleSegment(segments[segments.length - 1], SAMPLES);
  const extend = (from: PointTuple, toward: PointTuple): PointTuple => {
    const size = Math.hypot(from[0] - toward[0], from[1] - toward[1]) || 1;
    return [from[0] + (from[0] - toward[0]) / size * OVERRUN, from[1] + (from[1] - toward[1]) / size * OVERRUN];
  };
  const before = extend(head[0], head[1]);
  const after = extend(tail[tail.length - 1], tail[tail.length - 2]);
  return [{ kind: "line", start: before, end: head[0] }, ...segments, { kind: "line", start: tail[tail.length - 1], end: after }];
}

function path(segments: DraftSegment[]): PointTuple[] {
  return segments.flatMap((segment, index) => sampleSegment(segment, segment.kind === "bezier" ? SAMPLES : 1).slice(index === 0 ? 0 : 1));
}

function length(segments: DraftSegment[]): number {
  const samples = path(segments);
  let total = 0;
  for (let index = 1; index < samples.length; index += 1) total += Math.hypot(samples[index][0] - samples[index - 1][0], samples[index][1] - samples[index - 1][1]);
  return total;
}

function mix(a: PointTuple, b: PointTuple, amount: number): PointTuple { return [a[0] + (b[0] - a[0]) * amount, a[1] + (b[1] - a[1]) * amount]; }
